import { ConsoleLogger } from '@proposify/frontend-observability';
import { z } from 'zod';
import { Event } from '../../components/notifications/utils/EventTypes';
import type { NotificationLastKey } from './types';
import { trackValidationFailure } from './validationTracking';

/**
 * Zod schema for Event enum validation
 */
const EventSchema = z.nativeEnum(Event);

/**
 * Zod schema for individual notification feed item from API.
 *
 * This schema validates the raw API response structure.
 * Key validations:
 * - createdAt must be a valid ISO 8601 date string
 * - notificationId, userId, status, viewed are required
 * - documentId is optional in API response (can be at top level or nested in data as documentId)
 *
 * Note: Items without type OR documentId will be filtered by RenderableNotificationItemSchema.
 */
const NotificationFeedItemSchema = z
  .object({
    // Required fields
    notificationId: z.string().min(1, 'notificationId is required'),
    userId: z.string().min(1, 'userId is required'),
    status: z.string(),
    viewed: z.boolean().default(false), // Default to false if missing

    // Critical: Validate createdAt as valid ISO 8601 date string
    createdAt: z.string().datetime({
      message: 'createdAt must be a valid ISO 8601 date string',
    }),

    type: EventSchema.optional(),
    documentId: z.string().optional(),
    data: z.record(z.unknown()).default({}),
  })
  .passthrough(); // Allow unknown fields for future compatibility

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '');

const getDocumentIdFromData = (data: Record<string, unknown>): string | undefined => {
  return typeof data.documentId === 'string' && data.documentId.length > 0 ? data.documentId : undefined;
};

const ViewedNotificationDataSchema = z.record(z.unknown()).transform((data) => {
  const viewer =
    data.viewer && typeof data.viewer === 'object' && !Array.isArray(data.viewer) ? (data.viewer as Record<string, unknown>) : {};
  const firstName = typeof viewer.firstName === 'string' ? viewer.firstName.trim() : '';
  const lastName = typeof viewer.lastName === 'string' ? viewer.lastName.trim() : '';
  const email = typeof viewer.email === 'string' ? viewer.email : '';
  const viewerName = firstName || lastName ? [firstName, lastName].filter(Boolean).join(' ') : email;

  return {
    viewerName,
    companyName: toStringOrEmpty(data.companyName),
    documentName: toStringOrEmpty(data.documentName),
  };
});

/**
 * Schema for notifications that can be rendered in the UI.
 *
 * This refined schema ensures notifications have both type and documentId,
 * which are required for rendering in the notification feed.
 *
 * Filtering criteria:
 * - Must have type field (required to determine event rendering)
 * - Must have documentId (either at top-level or in data.documentId)
 *
 * Normalization:
 * - documentId is normalized to top-level (moved from data.documentId if needed)
 */
const RenderableNotificationItemSchema = NotificationFeedItemSchema.refine(
  (item) => {
    const documentId = item.documentId || getDocumentIdFromData(item.data);
    if (!item.type || !documentId) return false;
    return true;
  },
  {
    message: 'Notification must have both type and documentId to be renderable',
  }
).transform((item) => {
  // After refinement, we know type and documentId exist
  // Normalize documentId to top-level and shape data by notification type
  const documentId = item.documentId || getDocumentIdFromData(item.data);

  if (!item.type || !documentId) {
    throw new Error('Notification must have type and documentId');
  }

  const data = item.type === Event.VIEWED ? ViewedNotificationDataSchema.parse(item.data) : item.data;

  return {
    ...item,
    type: item.type,
    documentId: documentId,
    data,
  };
});

/**
 * Zod schema for lastKey object
 */
const LastKeySchema = z
  .object({
    createdAt: z.string().datetime(),
    notificationId: z.string().min(1),
  })
  .optional();

/**
 * Zod schema for the full notification feed response
 */
const NotificationsFeedResponseSchema = z
  .object({
    items: z.array(NotificationFeedItemSchema),
    lastKey: LastKeySchema,
  })
  .passthrough();

export type ValidatedNotificationFeedItem = z.infer<typeof NotificationFeedItemSchema>;
export type ValidatedNotificationsFeedResponse = z.infer<typeof NotificationsFeedResponseSchema>;

/**
 * Renderable notification item with required type and documentId.
 * The validation layer guarantees these fields are present.
 *
 * Note: We use intersection to explicitly mark type and documentId as required,
 * since Zod's refine/transform doesn't narrow optional fields in the inferred type.
 */
export type RenderableNotificationFeedItem = z.infer<typeof RenderableNotificationItemSchema> & {
  type: Event;
  documentId: string;
};

export type ValidationResult = {
  items: RenderableNotificationFeedItem[];
  lastKey: NotificationLastKey;
};

/**
 * Filters and validates items to ensure only renderable notifications are returned.
 *
 * @param items - Array of notification items to validate
 * @returns Array of validated renderable notification items
 */
const filterRenderableNotifications = (items: unknown[]): RenderableNotificationFeedItem[] => {
  return items
    .map((item, index) => {
      const itemResult = RenderableNotificationItemSchema.safeParse(item);
      if (!itemResult.success) {
        // Enhanced logging to show why item was filtered
        const hasType = !!(item as any)?.type;
        const hasDocumentId = !!(item as any)?.documentId || !!(item as any)?.data?.documentId;

        ConsoleLogger.warn(`Filtering out non-renderable notification at index ${index}:`, {
          notificationId: (item as any)?.notificationId,
          missingType: !hasType,
          missingDocumentId: !hasDocumentId,
          errors: itemResult.error.errors,
        });
        return null;
      }

      return itemResult.data;
    })
    .filter((item): item is RenderableNotificationFeedItem => item !== null);
};

/**
 * Validates notification feed responses from the API.
 *
 * This function ensures data integrity by validating the API response structure
 * and filtering out items that cannot be rendered in the UI.
 *
 * Key validations:
 * - createdAt must be valid ISO 8601 date string
 * - notificationId, userId, status, viewed are required
 * - type and documentId are required for rendering (items missing either are filtered out)
 * - documentId can be at top level or in data.documentId
 * - Invalid items are filtered out with warnings (graceful degradation)
 *
 * @param response - Raw API response
 * @returns Validated response with only renderable notification items
 */
export const validateNotificationsFeedResponse = (response: unknown): ValidationResult => {
  const responseObj = (response as Record<string, unknown>) || {};
  const result = NotificationsFeedResponseSchema.safeParse(response);

  if (!result.success) {
    ConsoleLogger.warn('Notification feed validation encountered issues:', {
      errors: result.error.errors,
      receivedData: response,
    });

    const originalItems = Array.isArray(responseObj.items) ? responseObj.items : [];
    const renderableIssues = originalItems
      .map((item, index) => {
        const hasType = !!(item as any)?.type;
        const hasDocumentId = !!(item as any)?.documentId || !!(item as any)?.data?.documentId;

        if (hasType && hasDocumentId) {
          return null;
        }

        return {
          index,
          notificationId: (item as any)?.notificationId,
          missingType: !hasType,
          missingDocumentId: !hasDocumentId,
        };
      })
      .filter((item) => item !== null);

    trackValidationFailure({
      label: 'Notification feed response',
      cause: 'notifications feed response schema validation failed',
      error: result.error,
      responseObj,
      extraContext: {
        renderableIssues,
        itemsCount: originalItems.length,
      },
    });

    const validatedItems = filterRenderableNotifications(originalItems);

    return {
      items: validatedItems,
      lastKey: responseObj.lastKey as NotificationLastKey,
    };
  }

  // Filter items to ensure only renderable notifications reach the UI
  const validatedItems = filterRenderableNotifications(result.data.items);

  return {
    ...result.data,
    items: validatedItems,
    lastKey: result.data.lastKey,
  };
};
